import { environment } from '@core/environment';
import { useCachedAuth0Url } from '@core/logic';
import type { TexturedMesh } from '@processingResults/types';
import { useEffect, useState } from 'react';
import { ShaderMaterial, Texture, TextureLoader } from 'three';

const textureLoader = new TextureLoader();

const vertexShader = `
uniform float meshXMin;
uniform float meshXSpan;
uniform float meshYMin;
uniform float meshYSpan;
varying vec2 texUV;

void main()	{
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  texUV = vec2(
    (position.x - meshXMin) / meshXSpan,
    (position.y - meshYMin) / meshYSpan
  );
}
`;

const fragmentShader = `
uniform sampler2D tex;
varying vec2 texUV;
      
void main()	{
  gl_FragColor = texture2D(tex, texUV);
}
`;

const formatVal = (value: number) => ({ value });

export const useTexture = (texturedMesh?: TexturedMesh): ShaderMaterial | undefined => {
  const [url, setUrl] = useState<string>();
  const chachedUrl = useCachedAuth0Url(url);
  const [texture, setTexture] = useState<Texture>();
  const [material, setMaterial] = useState<ShaderMaterial>();

  const boundingBox = texturedMesh?.texture?.boundingBox;

  // Cleanly dispose of texture on change or unmount
  useEffect(() => {
    return () => {
      texture?.dispose();
    };
  }, [texture]);

  // Cleanly dispose of material on change or unmount
  useEffect(() => {
    return () => {
      material?.dispose();
    };
  }, [material]);

  useEffect(() => {
    const gsPath = texturedMesh?.texture?.location;
    if (!texturedMesh || !gsPath || !gsPath.startsWith('gs://')) {
      if (url !== undefined) {
        setUrl(undefined);
        setTexture(undefined);
        setMaterial(undefined);
      }
      return;
    }

    const nextUrl = `${environment.apiUrl}/texturedMeshes/${texturedMesh.id}/downloads/texture`;
    if (url !== nextUrl) {
      setTexture(undefined);
      setMaterial(undefined);
      setUrl(nextUrl);
    }
  }, [texturedMesh, url]);

  useEffect(() => {
    if (!chachedUrl) return undefined;
    let cancelled = false;
    textureLoader
      .loadAsync(chachedUrl)
      .then((next) => {
        if (cancelled) {
          next.dispose();
          return;
        }
        setTexture(next);
      })
      .catch((err) => {
        if (!cancelled) console.error(err);
      });
    return () => {
      cancelled = true;
    };
  }, [chachedUrl]);

  useEffect(() => {
    if (!texture || !boundingBox) return;
    const { minX, maxX, minY, maxY } = boundingBox;
    const uniforms = {
      meshXMin: formatVal(minX),
      meshXSpan: formatVal(maxX - minX),
      meshYMin: formatVal(minY),
      meshYSpan: formatVal(maxY - minY),
      tex: {
        value: texture,
      },
    };
    const nextMaterial = new ShaderMaterial({
      uniforms,
      vertexShader,
      fragmentShader,
    });
    setMaterial(nextMaterial);
  }, [texture, boundingBox]);

  return material;
};
